/* =============================================================================
   KNN Mini Cart — custom cart drawer behaviour.
   - Opens from the cart icon (intercepts Horizon's cart trigger) and after add.
   - Refreshes its body via the Section Rendering API (section: knn-mini-cart).
   - Quantity change / remove / subscribe-toggle via the Cart AJAX API.
   - Subscribing a line: data-subscribe-variant means move to that variant (carrying
     data-selling-plan in the same add); data-selling-plan alone means attach the plan
     to the current line in place. The Liquid decides which; JS just honours it.
   - Free Gifts (assets/knn-free-gift.js): removing a qualifying parent takes its gifts
     with it, orphaned gifts are swept after every mutation, and upgrading a qualifying
     line to Subscribe & Save carries the gift across.
   - A successful Subscribe & Save upgrade pushes the shared `cart-subscribe-save-upgrade`
     GTM event (see trackSubscribeSaveUpgrade), ported from the previous store.
   ============================================================================= */
(function () {
  'use strict';

  var SECTION = 'knn-mini-cart';

  function freeGift() {
    return window.KNNFreeGift || null;
  }

  function El() {
    return document.querySelector('[data-knn-mini-cart]');
  }

  function open() {
    var el = El();
    if (!el) return;
    el.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    startTimer(el);
  }

  function close() {
    var el = El();
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.style.overflow = '';
  }

  /* ---- Refresh the drawer body from the Section Rendering API ---- */
  function refresh() {
    return fetch('/?sections=' + SECTION)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data[SECTION];
        if (html == null) return;
        var current = document.querySelector('[data-knn-mc-body]');
        // The section renders body_only, so its HTML IS the drawer body content.
        if (current) current.innerHTML = html;
        startTimer(El());
      })
      .catch(function (e) { console.error('[knn-mini-cart] refresh failed:', e); });
  }

  /* ---- Cart AJAX helpers ---- */
  function loaderHtml() {
    var root = El();
    var src = root && root.dataset.loaderSrc;
    if (!src) return '<div class="knn-mc__loader" aria-hidden="true"></div>';
    return '<div class="knn-mc__loader" aria-hidden="true"><img src="' + src + '" alt="" width="34" height="34"></div>';
  }

  /* Busy state for the WHOLE drawer, not the individual line — matching Vitalyra
     Health. Cart mutations run sequentially against line keys, so a second request
     fired while the first is in flight would act on a stale cart. The overlay both
     shows one centred spinner and blocks every other line's controls until the
     refresh lands. Guarded by a counter so nested calls (subscribe swap does add ->
     remove -> reassign) do not clear it early. */
  var busyDepth = 0;

  function setBusy(on) {
    busyDepth = Math.max(0, busyDepth + (on ? 1 : -1));

    var el = El();
    if (!el) return;
    /* The panel, not the body: the body scrolls, and it is also the element refresh()
       replaces wholesale — a spinner parented there would vanish mid-request. */
    var panel = el.querySelector('.knn-mc__panel');
    if (!panel) return;

    if (busyDepth > 0) {
      panel.classList.add('is-loading');
      if (!panel.querySelector('.knn-mc__loader')) {
        panel.insertAdjacentHTML('beforeend', loaderHtml());
      }
      return;
    }

    panel.classList.remove('is-loading');
    var loader = panel.querySelector('.knn-mc__loader');
    if (loader) loader.remove();
  }

  function changeLine(key, payload) {
    setBusy(true);
    var body = Object.assign({ id: key }, payload);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      /* A rejected change (variant unavailable, quantity refused) still answers 422 with
         a JSON body, so r.json() on its own reads as success. The status has to be
         checked, or a Subscribe & Save upgrade that never happened gets reported as a
         conversion — and the drawer keeps showing a quantity the cart does not hold. */
      .then(function (r) {
        if (r.ok) return r.json();
        return r.json().then(
          function (e) { throw new Error(e.description || e.message || 'Cart change failed'); },
          function () { throw new Error('Cart change failed (' + r.status + ')'); }
        );
      })
      .then(function () { return refresh(); })
      .then(function () { return syncCartCount(); })
      /* Resolves true/false so callers can tell a completed mutation from a swallowed
         failure — the Subscribe & Save tracking must not fire on a change that failed. */
      .then(function () { return true; })
      .catch(function (e) {
        console.error('[knn-mini-cart] change failed:', e);
        /* Re-render from the real cart, so a refused change never leaves the drawer
           showing the value the customer asked for rather than the one they have. */
        function no() { return false; }
        return refresh().then(no, no);
      })
      .then(function (ok) { setBusy(false); return ok; });
  }

  /* Removing a managed parent removes the gifts it earned, in one pass, so the drawer
     never renders an intermediate state with a stranded gift. Lines that are not managed
     parents fall through to the ordinary single-line removal. */
  function removeLine(key) {
    var fg = freeGift();
    if (!fg) return changeLine(key, { quantity: 0 });

    setBusy(true);

    return fg
      .fetchCartJson()
      .then(function (cart) {
        var keys = fg.collectLinesForParentRemoval(cart, key);
        if (!keys) return changeLine(key, { quantity: 0 });

        return fg
          .removeCartLinesTogether(keys)
          .then(function () { return refresh(); })
          .then(function () { return syncCartCount(); });
      })
      .catch(function (e) {
        console.error('[knn-mini-cart] managed remove failed:', e);
        return refresh().then(function () { return syncCartCount(); });
      })
      .then(function () { setBusy(false); });
  }

  /* The line items for a Subscribe & Save upgrade, plus any follow-up work the Free
     Gift programme needs once they are in the cart.

     Three cases, decided by the gift rules the Liquid attached to the control:

       no gift rules                the plain subscribe variant, as before
       Funnel 4 (subscription gift) the SnS variant + its gift, exactly as picking SnS
                                    3 Bottles on the PDP would — so an upgraded shopper
                                    gets the same gift as a direct one
       Funnel 6 (otp gift)          the shopper ALREADY holds the gifts against the
                                    one-time parent. Re-adding them would duplicate, so
                                    the new parent inherits the gift properties and the
                                    existing gift lines are re-pointed at it afterwards
                                    (reassign) rather than added again.
  */
  function buildSubscribeItems(cart, el, variantId, planId, qty) {
    var fg = freeGift();
    var plain = { id: parseInt(variantId, 10), quantity: qty };
    /* Omit rather than send a falsy plan — Shopify rejects the add otherwise. */
    if (planId) plain.selling_plan = parseInt(planId, 10);

    var raw = el && el.getAttribute('data-free-gift');
    var config = fg && raw ? fg.parseFreeGiftConfig(raw) : null;
    if (!config || !config.enabled) return { items: [plain], reassign: null };

    if (config.flow === 'old_funnel_subscription_gift') {
      var items = fg.buildOldFunnelSubscriptionGiftItems({
        config: config,
        variantId: variantId,
        quantity: qty,
        sellingPlanId: planId
      });
      return { items: fg.dropDuplicateGiftItems(cart, items), reassign: null };
    }

    if (config.flow === 'otp_gift') {
      var oldParent = el.getAttribute('data-parent-variant');
      var existing = fg.findGiftsForParent(cart, config.source, oldParent);
      if (!existing.length) return { items: [plain], reassign: null };

      plain.properties = fg.buildReassignedParentProperties(existing, config.source);
      return {
        items: [plain],
        reassign: { source: config.source, from: oldParent, to: String(variantId) }
      };
    }

    return { items: [plain], reassign: null };
  }

  /* Swap a one-time line to its subscribe counterpart variant: add the subscribe
     variant with the same quantity (and its Recharge selling plan, when the product
     has one), then remove the original one-time line. */
  function subscribeSwap(lineEl, el, variantId, planId) {
    setBusy(true);
    var fg = freeGift();
    var key = lineEl.dataset.knnMcLine;
    var input = lineEl.querySelector('[data-knn-mc-qty-input]');
    var qty = parseInt(input && input.value, 10) || 1;

    var load = fg ? fg.fetchCartJson() : Promise.resolve(null);

    return load
      .then(function (cart) {
        var plan = buildSubscribeItems(cart, el, variantId, planId, qty);

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: plan.items })
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw new Error(err.description || 'Add failed'); });
            return r.json();
          })
          .then(function () {
            /* The subscription line now exists, which is the moment the upgrade is
               real — track here, as previous store does, and before refresh() replaces
               the row we read the product details from. */
            trackSubscribeSaveUpgrade(lineEl);

            /* Drop the one-time line only after the subscription line exists, so the
               gifts are never briefly parentless. */
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          })
          .then(function () {
            if (!plan.reassign) return null;
            /* Re-read: the add and the removal both moved lines around. */
            return fg.fetchCartJson().then(function (fresh) {
              return fg.reassignGiftParents(fresh, plan.reassign.source, plan.reassign.from, plan.reassign.to);
            });
          });
      })
      .then(function () { return refresh(); })
      .then(function () { return syncCartCount(); })
      .catch(function (e) { console.error('[knn-mini-cart] subscribe swap failed:', e); return refresh(); })
      .then(function () { setBusy(false); });
  }

  /* Keep Horizon's header cart bubble in sync after a raw (non-Horizon) cart mutation.
     Horizon's <cart-icon> listens for 'shopify:cart:lines-update' and reads event.promise.
     The synthetic event carries no `action`, so our own add-listener ignores it. */
  function syncCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = (cart && cart.item_count) || 0;
        var evt = new Event('shopify:cart:lines-update', { bubbles: true });
        evt.promise = Promise.resolve({ cart: { totalQuantity: count }, detail: { itemCount: count } });
        document.dispatchEvent(evt);
      })
      .catch(function () {});
  }

  /* ---- Subscribe & Save upgrade tracking (GTM) ----
     Migrated from past store .js file → trackSubscribeSaveUpgrades so
     the same GTM trigger and container variables serve both stores. Event name, field
     names and value formatting are deliberately byte-identical to that implementation —
     the container matches on them, so "improving" any of these strings breaks reporting.

     Accepts either the switch/CTA that was operated or the line element itself; both
     resolve to the same cart line. Reads the line BEFORE the drawer is re-rendered, so
     the figures describe what the shopper upgraded FROM. */
  function trackSubscribeSaveUpgrade(el) {
    try {
      var line = el && el.closest ? el.closest('[data-knn-mc-line]') : null;
      var productHandle = (line && line.getAttribute('data-product-handle')) || '';
      var productTitle = (line && line.getAttribute('data-product-title')) || '';
      var variantTitle = (line && line.getAttribute('data-variant-title')) || '';

      /* A handle ending in a number names the funnel it belongs to
         (vitalyra-supergreens-6 → "funnel -6"); anything else reports "funnel -0".
         The space before the hyphen is not a typo — it is the literal value the GTM
         container matches on. */
      var funnelId = 'funnel -0';
      var handleParts = productHandle.split('-');
      var lastPart = handleParts[handleParts.length - 1];
      if (/^\d+$/.test(lastPart)) funnelId = 'funnel -' + lastPart;

      var bundleSize = variantTitle ? variantTitle.trim().toLowerCase().replace(/\s+/g, '-') : '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cart-subscribe-save-upgrade',
        productName: productTitle,
        productHandle: productHandle,
        funnel: funnelId,
        bundleSize: bundleSize,
        variantTitle: variantTitle,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.warn('[Track SnS Upgrade] Failed to push event:', err);
    }
  }

  /* ---- Delegated interactions inside the drawer ---- */
  function onClick(e) {
    var el = El();
    if (!el) return;

    if (e.target.closest('[data-knn-mc-overlay]') || e.target.closest('[data-knn-mc-close]')) {
      close();
      return;
    }

    var line = e.target.closest('[data-knn-mc-line]');

    /* Gift lines are rendered without quantity or remove controls; this guard is a
       belt-and-braces stop for anything that reaches a gift row another way. */
    if (line && line.hasAttribute('data-knn-mc-gift')) return;

    var dec = e.target.closest('[data-knn-mc-dec]');
    var inc = e.target.closest('[data-knn-mc-inc]');
    if ((dec || inc) && line) {
      var input = line.querySelector('[data-knn-mc-qty-input]');
      var q = parseInt(input.value, 10) || 1;
      q = inc ? q + 1 : q - 1;
      if (q < 0) q = 0;
      /* Managed bundles declare their own ceiling (the 6+2 parent caps at 2). */
      var max = parseInt(input.dataset.knnMcQtyMax, 10);
      if (!isNaN(max) && q > max) return;
      /* Reducing a parent to 0 is a removal, so route it through the managed path
         and take its gifts along. */
      if (q === 0) {
        removeLine(line.dataset.knnMcLine);
        return;
      }
      changeLine(line.dataset.knnMcLine, { quantity: q });
      return;
    }

    if (e.target.closest('[data-knn-mc-remove]') && line) {
      removeLine(line.dataset.knnMcLine);
      return;
    }

    // The gold "Switch to save" button (the switch checkbox is handled on change).
    var cta = e.target.closest('.knn-mc__switch-cta');
    if (cta && line) {
      applySubscription(line, cta);
      return;
    }
  }

  /* Subscribe a line, using whichever mode the markup declares:
     - data-subscribe-variant → the subscribe option is a DIFFERENT variant, so move
       the line there and take data-selling-plan along in the same add. Checked first:
       applying a plan to the one-time variant would leave the wrong price/pack.
     - data-selling-plan alone → the line is already on the right variant and only
       needs the plan attached, so apply it in place. */
  function applySubscription(line, el) {
    var planId = el.getAttribute('data-selling-plan');
    var variantId = el.getAttribute('data-subscribe-variant');
    if (variantId) {
      subscribeSwap(line, el, variantId, planId);
      return;
    }
    if (!planId) return;
    changeLine(line.dataset.knnMcLine, { selling_plan: planId }).then(function (ok) {
      /* `line` is detached by the refresh inside changeLine, but it still carries its
         data attributes and closest() matches itself — so the tracked details are the
         pre-upgrade ones, as in the swap path. */
      if (ok) trackSubscribeSaveUpgrade(line);
    });
  }

  function onChange(e) {
    // Subscribe switch → selling plan (Recharge) or variant swap, per the markup.
    var sw = e.target.closest('.knn-mc__switch-input[data-knn-mc-subscribe]');
    if (sw) {
      if (!sw.checked) return;
      var line = sw.closest('[data-knn-mc-line]');
      if (line) applySubscription(line, sw);
      return;
    }

    // Editable quantity field → update the line quantity (blur / Enter fires change).
    var qty = e.target.closest('[data-knn-mc-qty-input]');
    if (qty) {
      var qline = qty.closest('[data-knn-mc-line]');
      if (!qline) return;
      if (qline.hasAttribute('data-knn-mc-gift')) { refresh(); return; }
      var val = parseInt(String(qty.value).replace(/[^0-9]/g, ''), 10);
      if (isNaN(val)) {
        refresh(); // revert to the real cart value
        return;
      }
      var qmax = parseInt(qty.dataset.knnMcQtyMax, 10);
      if (!isNaN(qmax) && val > qmax) {
        refresh(); // revert: a managed bundle cannot exceed its cap
        return;
      }
      if (val === 0) {
        removeLine(qline.dataset.knnMcLine);
        return;
      }
      changeLine(qline.dataset.knnMcLine, { quantity: val });
    }
  }

  // Commit the typed quantity on Enter without waiting for blur.
  function onKeydownQty(e) {
    if (e.key !== 'Enter') return;
    var qty = e.target.closest('[data-knn-mc-qty-input]');
    if (qty) {
      e.preventDefault();
      qty.blur();
    }
  }

  /* ---- Countdown timer (per-visitor, persisted) ---- */
  var timerHandle = null;
  function startTimer(el) {
    if (!el) return;
    var timer = el.querySelector('[data-knn-mc-timer]');
    var clock = el.querySelector('[data-knn-mc-clock]');
    if (!timer || !clock) return;

    var minutes = parseInt(timer.getAttribute('data-minutes'), 10) || 10;
    var key = 'knn-mc-timer-end';
    var end = parseInt(localStorage.getItem(key), 10);
    var now = Date.now();
    if (!end || end < now) {
      end = now + minutes * 60 * 1000;
      localStorage.setItem(key, String(end));
    }

    if (timerHandle) clearInterval(timerHandle);
    function tick() {
      var remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
      var m = Math.floor(remaining / 60);
      var s = remaining % 60;
      clock.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      if (remaining <= 0 && timerHandle) clearInterval(timerHandle);
    }
    tick();
    timerHandle = setInterval(tick, 1000);
  }

  /* ---- Wire up ---- */
  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
    document.addEventListener('keydown', onKeydownQty);

    // Open from the KNN offer widget after an item is added (raw fetch → sync bubble).
    document.addEventListener('knn:cart:added', function () {
      refresh().then(open);
      syncCartCount();
    });

    // Sitewide coverage: when Horizon adds an item anywhere, open the KNN drawer.
    // Only react to real "add" events (they carry an `action`); ignore our synthetic
    // count-sync events (no `action`) and update/remove events.
    document.addEventListener('shopify:cart:lines-update', function (e) {
      if (e && e.action === 'add') {
        refresh().then(open);
      }
    });

    // Intercept the header cart icon so it opens the KNN drawer instead of Horizon's.
    document.addEventListener(
      'click',
      function (e) {
        var trigger = e.target.closest(
          'a[href$="/cart"], a[href*="/cart?"], [data-knn-open-cart], .header-actions__cart-icon, cart-icon'
        );
        if (!trigger) return;
        if (e.target.closest('[data-knn-mini-cart]')) return; // ignore clicks inside the drawer
        e.preventDefault();
        e.stopPropagation();
        open();
      },
      true // capture, so we beat Horizon's own handler
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
